/**
 * API Client Interceptors
 *
 * Centralized interceptor setup for XP and Content API clients.
 * Handles authentication, authorization, and impersonation errors at the request/response level.
 *
 * Uses handleApiError as the single source of truth for error handling logic.
 *
 * IMPORTANT: We use RESPONSE interceptors (not error interceptors) because:
 * - Response interceptors run BEFORE the response.ok check
 * - We can convert error responses to "fake OK" responses to prevent React Query errors
 * - Error interceptors run too late - the error still gets thrown even if we return undefined
 */

import { handleApiError } from '@/utils/api-error-handler';
import { getApiToken } from '@/utils/auth';

import { client as contentClient } from '@/_generated/api-clients/content/client.gen';
import { client as xpClient } from '@/_generated/api-clients/xp/client.gen';
import { store as jotaiStore } from '@/atoms';
import { sessionOverridesAtom } from '@/atoms/sessionOverrides';

/**
 * Track handled errors to prevent duplicate toasts.
 * Errors are auto-cleared after a delay to prevent memory leaks
 * while still avoiding infinite toast loops during active error states.
 */
const handledAuthErrors = new Map<string, NodeJS.Timeout>();

/**
 * Generate a deduplication key for an error.
 * Uses URL to group errors from the same endpoint.
 */
const getErrorKey = (request: Request): string => request.url;

/**
 * Check if we've already handled this error recently.
 */
const hasHandledError = (key: string): boolean => handledAuthErrors.has(key);

/**
 * Mark an error as handled with auto-cleanup after 60 seconds.
 * This is long enough to prevent infinite loops during active error states,
 * but short enough to prevent memory leaks during long sessions.
 */
const markErrorHandled = (key: string): void => {
  // Clear any existing timeout for this key
  const existingTimeout = handledAuthErrors.get(key);
  if (existingTimeout) {
    clearTimeout(existingTimeout);
  }

  // Set new timeout for cleanup
  const timeout = setTimeout(() => {
    handledAuthErrors.delete(key);
  }, 60_000); // 60 seconds

  handledAuthErrors.set(key, timeout);
};

/**
 * Clear a handled error (for manual retry scenarios).
 * Exported so it can be called when user explicitly retries.
 */
export const clearHandledError = (key: string): void => {
  const timeout = handledAuthErrors.get(key);
  if (timeout) {
    clearTimeout(timeout);
  }
  handledAuthErrors.delete(key);
};

/**
 * Clear all handled errors (e.g., when impersonation ends).
 */
export const clearAllHandledErrors = (): void => {
  // Clear all timeouts
  for (const timeout of handledAuthErrors.values()) {
    clearTimeout(timeout);
  }
  handledAuthErrors.clear();
};

/**
 * Create a synthetic "OK" response to replace error responses we've handled.
 * This prevents the error from propagating to React Query.
 */
const createSyntheticOkResponse = (originalResponse: Response): Response =>
  new Response(JSON.stringify({}), {
    status: 200,
    statusText: 'OK (handled by interceptor)',
    headers: originalResponse.headers,
  });

/**
 * Setup request interceptor for a client.
 * Adds auth token and session override headers.
 */
const setupRequestInterceptor = (
  client: typeof xpClient | typeof contentClient,
) => {
  client.interceptors.request.use(async req => {
    const token = await getApiToken();
    if (token) {
      req.headers.set('Authorization', `Bearer ${token}`);
    }

    // Apply session override headers
    const sessionOverrides = jotaiStore.get(sessionOverridesAtom);
    if (sessionOverrides.has('updateResource')) {
      req.headers.set('X-Pathway-Feature-HideAllXylemeReferences', 'true');
    }

    return req;
  });
};

/**
 * Setup response interceptor for a client.
 * Intercepts error responses BEFORE they become errors to prevent infinite loops.
 *
 * This runs before the response.ok check, so we can:
 * 1. Detect auth errors (401/403)
 * 2. Show toast and mark as handled
 * 3. Return a synthetic "OK" response to prevent error propagation
 */
const setupResponseInterceptor = (
  client: typeof xpClient | typeof contentClient,
) => {
  client.interceptors.response.use(async (response, request, _options) => {
    // Only intercept error responses
    if (response.ok) {
      return response;
    }

    const errorKey = getErrorKey(request);

    // Already handled - return synthetic OK to prevent error
    if (hasHandledError(errorKey)) {
      return createSyntheticOkResponse(response);
    }

    // Create error object with status for handleApiError
    const errorInfo = {
      status: response.status,
      statusText: response.statusText,
      url: request.url,
    };

    // handleApiError returns true if it handled the error (impersonation toast)
    // throws if logout needed, returns false for unhandled errors
    const wasHandled = await handleApiError(errorInfo, 'Interceptor');

    if (wasHandled) {
      // Error was handled (toast shown for impersonation)
      // Return synthetic OK response to prevent error propagation
      markErrorHandled(errorKey);
      return createSyntheticOkResponse(response);
    }

    // Error was not handled - let it continue as normal error
    return response;
  });
};

/**
 * Initialize all API client interceptors.
 * Call this once at app startup (in main.tsx).
 */
export const initializeApiInterceptors = (): void => {
  // Setup XP client
  setupRequestInterceptor(xpClient);
  setupResponseInterceptor(xpClient);

  // Setup Content client
  setupRequestInterceptor(contentClient);
  setupResponseInterceptor(contentClient);

  console.debug('[API] Interceptors initialized for XP and Content clients');
};

/**
 * Re-export clients for convenience.
 */
export { contentClient, xpClient };
